import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  CircleNotch,
  CheckCircle,
  Clock,
  XCircle,
  ArrowsClockwise,
  Trash
} from '@phosphor-icons/react';


function PaymentStatusBadge({ status }) {
  const config = {
    'paid': { icon: CheckCircle, color: 'text-green-600 bg-green-50', label: 'Pagado' },
    'pending': { icon: Clock, color: 'text-yellow-600 bg-yellow-50', label: 'Pendiente' },
    'initiated': { icon: Clock, color: 'text-blue-600 bg-blue-50', label: 'Iniciado' },
    'expired': { icon: XCircle, color: 'text-red-600 bg-red-50', label: 'Expirado' },
    'failed': { icon: XCircle, color: 'text-red-600 bg-red-50', label: 'Fallido' },
  };

  const { icon: Icon, color, label } = config[status] || config.pending;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>
      <Icon size={14} weight="fill" />
      {label}
    </span>
  );
}

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/payments/history`, {
        withCredentials: true
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async (sessionId) => {
    try {
      await axios.get(`${API_URL}/api/payments/status/${sessionId}`, {
        withCredentials: true
      });
      fetchPayments();
    } catch (error) {
      console.error('Error refreshing status:', error);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('¿Eliminar este registro de pago? Esta accion no se puede deshacer.')) return;
    try {
      await axios.delete(`${API_URL}/api/payments/${paymentId}`, { withCredentials: true });
      fetchPayments();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar pago');
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pagos</h1>
          <p className="text-slate-500">Historial de transacciones de pago</p>
        </div>
        <button
          onClick={fetchPayments}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
          data-testid="refresh-button"
        >
          <ArrowsClockwise size={20} />
          Actualizar
        </button>
      </div>

      {/* Payments List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch size={32} className="animate-spin text-blue-600" />
          </div>
        ) : payments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="payments-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Cotización
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Cliente
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Tipo
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Monto
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payments.map((payment) => (
                  <tr key={payment.id} className="table-row-hover" data-testid={`payment-row-${payment.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{payment.quote_number}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {payment.client_name}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-600 capitalize">
                        {payment.payment_type === 'deposit' ? 'Depósito' : 'Pago Total'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">
                        ${payment.amount?.toLocaleString()}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(payment.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4">
                      <PaymentStatusBadge status={payment.payment_status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {payment.payment_status !== 'paid' && (
                          <button
                            onClick={() => refreshStatus(payment.session_id)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
                            title="Actualizar estado"
                            data-testid={`refresh-${payment.id}`}
                          >
                            <ArrowsClockwise size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePayment(payment.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors text-slate-400 hover:text-red-600"
                          title="Eliminar pago"
                          data-testid={`delete-payment-${payment.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <p className="text-slate-500">No hay transacciones de pago</p>
          </div>
        )}
      </div>
    </div>
  );
}
